'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import cl from 'clsx/lite';
import { forwardRef, useRef, useId, useState, useEffect, useCallback, createContext } from 'react';
import { useMergeRefs } from '../../utilities/hooks/use-merge-refs/use-merge-refs.js';
import '@digdir/designsystemet-web';

const text = (el) => el.textContent?.trim() || '';
const sanitizeItems = (values = []) => typeof values === 'string'
    ? [{ label: values, value: values }]
    : !Array.isArray(values)
        ? [values]
        : values.map((value) => typeof value === 'string' ? { label: value, value } : value);
const nextItems = (data, prev, multiple) => {
    const item = { label: text(data), value: data.value };
    if (!multiple)
        return data.isConnected ? undefined : item;
    return data.isConnected
        ? sanitizeItems(prev).filter(({ value }) => value !== item.value)
        : [...sanitizeItems(prev), item];
};
const defaultFilter = ({ label, input }) => label.toLowerCase().includes(input.value.trim().toLowerCase());
const Suggestion = forwardRef(function Suggestion({ children, className, creatable = false, defaultSelected, filter = true, multiple = false, name, onBeforeMatch, onSelectedChange, renderSelected = ({ label }) => label, selected, ...rest }, ref) {
    const dsSuggestionRef = useRef(null);
    const genId = useId();
    const selectId = rest.id ? `${rest.id}-select` : genId;
    const isControlled = selected !== undefined;
    const mergedRefs = useMergeRefs([ref, dsSuggestionRef]);
    const [isEmpty, setIsEmpty] = useState(false);
    const [defaultItems, setDefaultItems] = useState(sanitizeItems(defaultSelected));
    const selectedItems = selected ? sanitizeItems(selected) : defaultItems;
    const onSelectedChangeRef = useRef(onSelectedChange);
    const selectedItemsRef = useRef(selectedItems);
    // Keep the ref updated with the latest callback
    useEffect(() => {
        onSelectedChangeRef.current = onSelectedChange;
    }, [onSelectedChange]);
    useEffect(() => {
        selectedItemsRef.current = selectedItems;
    }, [selectedItems]);
    /**
     * Listerners and handling of adding/removing
     */
    useEffect(() => {
        const combobox = dsSuggestionRef.current;
        const beforeChange = (event) => {
            event.preventDefault();
            const multiple = combobox?.multiple;
            const data = event.detail;
            const nextItem = nextItems(data, selectedItemsRef.current, multiple);
            onSelectedChangeRef.current?.(nextItem || null);
            if (!isControlled)
                setDefaultItems(sanitizeItems(nextItem));
        };
        combobox?.addEventListener('comboboxbeforeselect', beforeChange);
        return () => combobox?.removeEventListener('comboboxbeforeselect', beforeChange);
    }, [isControlled]);
    // Before match event listener
    useEffect(() => {
        const combobox = dsSuggestionRef.current;
        const beforeMatch = (e) => onBeforeMatch?.(e);
        combobox?.addEventListener('comboboxbeforematch', beforeMatch);
        return () => combobox?.removeEventListener('comboboxbeforematch', beforeMatch);
    }, [onBeforeMatch]);
    const handleFilter = useCallback(() => {
        const { control: input, options = [] } = dsSuggestionRef?.current || {};
        const filterFn = filter === true ? defaultFilter : filter;
        let disabled = 0;
        let index = 0;
        for (const option of options)
            if (!option.hasAttribute('data-empty')) {
                if (filterFn && input)
                    option.disabled =
                        !filterFn({
                            index,
                            input,
                            label: option.label,
                            optionElement: option,
                            text: option.text,
                            value: option.value,
                        }) && Boolean(++disabled);
                index++; // Increment index for each <option>
            }
        setIsEmpty(index === disabled);
    }, [filter]);
    return (jsx(SuggestionContext.Provider, { value: { isEmpty, handleFilter, dsSuggestionRef }, children: jsxs("ds-suggestion", { "data-multiple": multiple || undefined, "data-creatable": creatable || undefined, class: cl('ds-suggestion', className), ref: mergedRefs, suppressHydrationWarning // Since <ds-suggestion> adds attributes
            : true, ...rest, children: [selectedItems.map((item) => (jsx("data", { value: item.value, children: renderSelected(item) }, item.value))), children, !!name && (jsx("select", { name: name, multiple: true, hidden: true, id: selectId }))] }) }));
});
const SuggestionContext = createContext({
    handleFilter: () => undefined,
});

export { Suggestion, SuggestionContext };
